import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditableTextInline from '../../editors/EditableTextInline';
import { EditableBooleanInline } from '../../editors/EditableBooleanInline_2';
import { LprConfigCategory, LprConfigParam } from './types/lpr';

interface LprConfigListProps {
  categories: LprConfigCategory[];
  params: LprConfigParam[];
  onParamSave: (paramId: string, newValue: string) => void;
}

export const LprConfigList: React.FC<LprConfigListProps> = ({
  categories,
  params,
  onParamSave,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'ru';

  // Хелпер для локализации из JSONB (title_i18n, desc_i18n)
  const getI18nText = (textObj: Record<string, string> = {}) => {
    return textObj[lang] || textObj['en'] || textObj['ru'] || '';
  };

  // Сортировка категорий по ord_num
  const sortedCategories = [...categories].sort((a, b) => a.ord_num - b.ord_num);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 800, mx: 'auto', p: 2 }}>
      {sortedCategories.map((cat) => {
        // Фильтруем и сортируем параметры для текущей категории
        const catParams = params
          .filter((p) => p.category_id === cat.category_id)
          .sort((a, b) => a.ord_num - b.ord_num);

        return (
          <Paper key={cat.category_id} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            {/* Заголовок и описание категории */}
            <Typography variant="h6" component="h2" fontWeight="bold">
              {getI18nText(cat.title_i18n)}
            </Typography>
            {getI18nText(cat.desc_i18n) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {getI18nText(cat.desc_i18n)}
              </Typography>
            )}

            <Divider sx={{ my: 1.5 }} />

            {/* Список параметров категории */}
            <List disablePadding>
              {catParams.map((param, index) => {
                const title = getI18nText(param.title_i18n);
                const desc = getI18nText(param.desc_i18n);
                // Если значение у клиента переопределено — берем value, иначе default_value
                const currentValue = param.value ?? param.default_value ?? '';
                const isBoolean = param.param_type === 'boolean';

                // Приведение строки из БД/API к булевому типу
                const boolValue =
                  typeof currentValue === 'boolean'
                    ? currentValue
                    : String(currentValue).toLowerCase() === 'true' || String(currentValue) === '1';

                return (
                  <React.Fragment key={param.param_id}>
                    {index > 0 && <Divider component="li" light />}
                    <ListItem
                      sx={{
                        py: 2,
                        px: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      {/* Левая часть: Название и иконка с подсказкой */}
                      <Box display="flex" alignItems="center" gap={1} sx={{ width: '40%', flexShrink: 0 }}>
                        <Typography variant="subtitle2">{title}</Typography>
                        {desc && (
                          <Tooltip title={desc} arrow placement="top">
                            <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'pointer' }} />
                          </Tooltip>
                        )}
                      </Box>

                      {/* Правая часть: Выбор нужного редактора по типу */}
                      <Box sx={{ width: '55%', display: 'flex', justifyContent: 'flex-end' }}>
                        {isBoolean ? (
                          <EditableBooleanInline
                            value={boolValue}
                            labelTrue={t('common.yes', 'Yes')}
                            labelFalse={t('common.no', 'No')}
                            onSave={(newBoolValue) =>
                              onParamSave(param.param_id, String(newBoolValue))
                            }
                          />
                        ) : (
                          <EditableTextInline
                            value={currentValue}
                            type={param.param_type === 'number' ? 'number' : 'text'}
                            multiline={param.param_type === 'json'}
                            width="100%"
                            onSave={(newValue) => onParamSave(param.param_id, newValue)}
                          />
                        )}
                      </Box>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        );
      })}
    </Box>
  );
};